import { useState, useRef, useEffect } from "react";
import {
  ChatBubbleLeftIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

function ChatHistory({
  savedChats = [],
  archivedChats = [],
  onChatClick,
  currentChatId,
  onArchiveChat,
  onRestoreChat,
  onRenameChat,
}) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editingChatId, setEditingChatId] = useState(null);
  const [editedTitle, setEditedTitle] = useState("");
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [chatToArchive, setChatToArchive] = useState(null);
  const [showArchivedChats, setShowArchivedChats] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // Group chats by time period
  const groupChatsByTime = (chats) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);

    const groups = {
      today: [],
      yesterday: [],
      lastWeek: [],
      lastMonth: [],
      older: [],
    };

    chats.forEach((chat) => {
      const chatDate = new Date(chat.createdAt);
      if (chatDate >= today) {
        groups.today.push(chat);
      } else if (chatDate >= yesterday) {
        groups.yesterday.push(chat);
      } else if (chatDate >= lastWeek) {
        groups.lastWeek.push(chat);
      } else if (chatDate >= lastMonth) {
        groups.lastMonth.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    const sections = [];
    if (groups.today.length > 0)
      sections.push({ title: "Today", chats: groups.today });
    if (groups.yesterday.length > 0)
      sections.push({ title: "Yesterday", chats: groups.yesterday });
    if (groups.lastWeek.length > 0)
      sections.push({ title: "Last 7 Days", chats: groups.lastWeek });
    if (groups.lastMonth.length > 0)
      sections.push({ title: "Last 30 Days", chats: groups.lastMonth });
    if (groups.older.length > 0)
      sections.push({ title: "Older", chats: groups.older });

    return sections;
  };

  const chatSections =
    savedChats.length > 0 ? groupChatsByTime(savedChats) : [];
  const archivedChatSections =
    archivedChats.length > 0 ? groupChatsByTime(archivedChats) : [];

  const handleChatClick = (chat) => {
    if (onChatClick) {
      onChatClick(chat);
    }
  };

  const handleMenuToggle = (chatId, e) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === chatId ? null : chatId);
  };

  const handleRenameClick = (chat, e) => {
    e.stopPropagation();
    setEditingChatId(chat.id);
    setEditedTitle(chat.title);
    setOpenMenuId(null);
  };

  const handleRenameSave = (chatId) => {
    if (editedTitle.trim() && onRenameChat) {
      onRenameChat(chatId, editedTitle.trim());
    }
    setEditingChatId(null);
    setEditedTitle("");
  };

  const handleRenameCancel = () => {
    setEditingChatId(null);
    setEditedTitle("");
  };

  const handleArchiveClick = (chat, e) => {
    e.stopPropagation();
    setChatToArchive(chat);
    setShowArchiveConfirm(true);
    setOpenMenuId(null);
  };

  const handleArchiveConfirm = () => {
    if (chatToArchive && onArchiveChat) {
      onArchiveChat(chatToArchive.id);
    }
    setShowArchiveConfirm(false);
    setChatToArchive(null);
  };

  const handleArchiveCancel = () => {
    setShowArchiveConfirm(false);
    setChatToArchive(null);
  };

  const handleRestoreClick = (chat, e) => {
    e.stopPropagation();
    if (onRestoreChat) {
      onRestoreChat(chat.id);
    }
    setOpenMenuId(null);
  };

  const renderChatList = (sections, isArchived = false) => (
    <>
      {sections.map((section, sectionIndex) => (
        <div key={sectionIndex}>
          <div className="flex items-center gap-2 mb-2 px-2">
            <ClockIcon className="h-4 w-4 text-gray-400" />
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              {section.title}
            </h3>
          </div>

          <div className="space-y-1 mb-6">
            {section.chats.map((chat) => (
              <div key={chat.id} className="relative group/item">
                {editingChatId === chat.id ? (
                  // Rename mode
                  <div className="p-2">
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleRenameSave(chat.id);
                        } else if (e.key === "Escape") {
                          handleRenameCancel();
                        }
                      }}
                      onBlur={() => handleRenameSave(chat.id)}
                      className="w-full px-2 py-1 text-sm border border-[#6036C4] rounded focus:outline-none focus:ring-2 focus:ring-[#6036C4] focus:ring-opacity-20"
                      autoFocus
                    />
                  </div>
                ) : (
                  // Normal display mode
                  <button
                    onClick={() => handleChatClick(chat)}
                    className={`w-full text-left p-2 pr-8 rounded-md transition-colors focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6036C4] ${
                      currentChatId === chat.id
                        ? "bg-gray-200"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <ChatBubbleLeftIcon className="h-4 w-4 text-gray-400 transition-colors" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600 line-clamp-2 leading-snug font-normal">
                          {chat.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {chat.timeAgo}
                        </p>
                      </div>
                    </div>
                  </button>
                )}

                {/* Hamburger menu button */}
                {editingChatId !== chat.id && (
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={(e) => handleMenuToggle(chat.id, e)}
                      className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 opacity-0 group-hover/item:opacity-100 transition-opacity"
                      aria-label="Chat options"
                    >
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </button>

                    {/* Dropdown menu */}
                    {openMenuId === chat.id && (
                      <div
                        ref={menuRef}
                        className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50"
                      >
                        <button
                          onClick={(e) => handleRenameClick(chat, e)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <PencilIcon className="h-4 w-4" />
                          Rename
                        </button>
                        {isArchived ? (
                          <button
                            onClick={(e) => handleRestoreClick(chat, e)}
                            className="w-full text-left px-3 py-2 text-sm text-green-600 hover:bg-green-50 flex items-center gap-2"
                          >
                            <ArrowUturnLeftIcon className="h-4 w-4" />
                            Restore chat
                          </button>
                        ) : (
                          <button
                            onClick={(e) => handleArchiveClick(chat, e)}
                            className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                          >
                            <ArchiveBoxIcon className="h-4 w-4" />
                            Archive
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="space-y-6 flex flex-col h-full">
      {/* Active Chats */}
      <div className="flex-1 overflow-y-auto">
        {renderChatList(chatSections, false)}

        {chatSections.length === 0 && (
          <div className="text-center py-8">
            <ChatBubbleLeftIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-sm text-gray-500">No chat history yet</p>
            <p className="text-xs text-gray-400 mt-1">
              Start a conversation to see your chat history
            </p>
          </div>
        )}
      </div>

      {/* Archived Chats Section */}
      {archivedChats.length > 0 && (
        <div className="border-t border-gray-200 pt-3 mt-auto">
          <button
            onClick={() => setShowArchivedChats(!showArchivedChats)}
            className="w-full flex items-center justify-between px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <div className="flex items-center gap-2">
              <ArchiveBoxIcon className="h-4 w-4" />
              <span>Archived Chats ({archivedChats.length})</span>
            </div>
            {showArchivedChats ? (
              <ChevronUpIcon className="h-4 w-4" />
            ) : (
              <ChevronDownIcon className="h-4 w-4" />
            )}
          </button>

          {showArchivedChats && (
            <div className="mt-3 space-y-6 max-h-64 overflow-y-auto">
              {renderChatList(archivedChatSections, true)}
            </div>
          )}
        </div>
      )}

      {/* Archive Confirmation Dialog */}
      {showArchiveConfirm && (
        <div
          className="fixed inset-0 backdrop-blur-sm flex items-center justify-center z-[200]"
          onClick={handleArchiveCancel}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Archive chat?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              This chat will be moved to your archived chats. You can restore it
              anytime.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleArchiveCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 transition-colors"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatHistory;
